/** The overworld of castles */
import kontra from 'kontra';

import BaseScene from './base';
import { SCREEN_WIDTH, SCREEN_HEIGHT, CASTLE_LOCATIONS, CASTLE_CONNECTIONS, CASTLE_NAMES, EDGE_DAMAGE_PROBABILITY } from '../constants';
import { shuffle } from '../utilities';

import grassImage from '../../assets/grass.png';
import uiTexture from '../../assets/ui.png';
import travelButton from '../../assets/travelButton.png';

import Castle from '../entities/castle';
import Edge from '../entities/edge';
import Particles from '../particles';

const { keyPressed, emit, track, imageAssets, on, off, SpriteSheet, Sprite } = kontra;

const getCastleLocations = () => {
  return CASTLE_LOCATIONS.map(p => {
    return {
      x: p.x + 128 * (0.5 - Math.random()),
      y: p.y + 128 * (0.5 - Math.random())
    };
  });
};

const getCastleConnections = () => {
  return CASTLE_CONNECTIONS;
};

export default class OverworldScene extends BaseScene {
  constructor () {
    super();
    this.uiPattern = this.context.createPattern(imageAssets[uiTexture], 'repeat');
    this.grassPattern = this.context.createPattern(imageAssets[grassImage], 'repeat');

    this.uiWrapper = document.querySelector('.overworld-info');
    this.currentNameElement = document.querySelector('.castle-name');
    this.destinationNameElement = document.querySelector('.destination-name');
    this.linkStrengthElement = document.querySelector('.link-strength');
    this.daysLeftElement = document.querySelector('.days-remaining');
    this.engineerHealthElement = document.querySelector('.engineer-health');
    this.networkHealthElement = document.querySelector('.network-health');
    this.instructionsElement = document.querySelector('.instructions');
    this.instructionsButton = document.querySelector('.close-instructions');

    this.linkSeveredElement = document.querySelector('.link-severed');
    this.linkSeveredButton = document.querySelector('.link-severed button');

    on('reset-castles', () => {
      this.castles = undefined;
    });
  }

  onEnter () {
    if (!this.castles) {
      // show but also hide instructions
      this.instructionsElement.classList.add('visible');
      this.instructionsButton.addEventListener('click', () => {
        this.instructionsElement.classList.remove('visible');
      });

      this.linkSeveredButton.addEventListener('click', () => {
        this.linkSeveredElement.classList.remove('visible');
      });

      this.daysLeft = 14;
      this.engineerHealth = 20;

      this.castles = [];
      this.edges = [];

      this.travelSheet = SpriteSheet({
        image: imageAssets[travelButton],
        frameWidth: 128,
        frameHeight: 32,
        animations: {
          unpressed: {
            frames: [0],
            frameRate: 60
          },
          pressed: {
            frames: [1],
            frameRate: 60
          }
        }
      });

      this.travelButton = Sprite({
        x: 160,
        y: 400,
        onDown: () => { this.travelButton.playAnimation('pressed'); },
        onUp: () => {
          this.toTunnel();
        },
        anchor: { x: 0.5, y: 0.5 },
        animations: this.travelSheet.animations
      });

      track(this.travelButton);

      const shuffledNames = shuffle(CASTLE_NAMES);

      getCastleLocations().map((location, idx) => {
        this.castles.push(new Castle({
          name: shuffledNames[idx],
          x: location.x,
          y: location.y
        }));
      });

      getCastleConnections().map((row, i) => {
        row.map((element, k) => {
          if (element) {
            this.edges.push(new Edge({ c1: this.castles[i], c2: this.castles[k] }));
          }
        });
      });

      this.currentCastle = this.castles[Math.floor(Math.random() * this.castles.length)];
      this.currentNameElement.innerText = this.currentCastle.name;
      this.currentCastle.inhabited = true;

      this.totalEdges = this.edges.length;

      this.edges.map(e => {
        if (e.c1 === this.currentCastle) {
          e.c2.candidate = true;
        } else if (e.c2 === this.currentCastle) {
          e.c1.candidate = true;
        }
      });

      this.castles.map(track);

      this.boundSetHealth = this.setHealth.bind(this);
      this.boundTargetCastle = this.targetACastle.bind(this);
      this.boundDestroyLink = this.destroyLink.bind(this);

      this.networkHealthElement.innerText = this.getNetworkHealthString();
      this.uiWrapper.classList.add('active');
    } else {
      this.doTravel();
    }

    on('target-castle', this.boundTargetCastle);
    on('overworld-health', this.boundSetHealth);
    on('link-died', this.boundDestroyLink);

    if (this.getNetworkHealth() < 33) {
      emit('scene-change', 'coward');
    }

    this.daysLeftElement.innerText = this.daysLeft;
    this.engineerHealthElement.innerText = this.engineerHealth;
  }

  destroyLink () {
    this.edges = this.edges.filter(e => {
      return e !== this.activeEdge;
    });
    this.activeEdge = undefined;
    this.castles.map(c => { c.target = false; });
    this.targetCastle = this.currentCastle;
    this.linkSeveredElement.classList.add('visible');
  }

  onExit () {
    off('target-castle', this.boundTargetCastle);
    off('edge-damage', this.boundEdgeDamage);
    off('overworld-health', this.boundSetHealth);
    this.uiWrapper.classList.remove('active');
  }

  setHealth ({ player, link }) {
    this.engineerHealth = player;
    this.engineerHealthElement.innerText = player;
    this.activeEdge.health = link;
  }

  targetACastle (c) {
    this.targetCastle = c;
    this.edges.map(e => {
      e.active = false;
      if (
        (e.c1 === c && e.c2 === this.currentCastle) ||
            (e.c2 === c && e.c1 === this.currentCastle)) {
        e.active = true;
        this.activeEdge = e;
        this.destinationNameElement.innerText = this.targetCastle.name;
        this.linkStrengthElement.innerText = this.activeEdge.getHealthString();
      }
    });
  }

  update () {
    this.castles.map(c => c.update());
    this.edges.map(e => e.update());
    this.travelButton.update();
  }

  toTunnel () {
    emit('scene-change', 'tunnel');
    emit('health', { player: this.engineerHealth, network: this.activeEdge.health });
  }

  getNetworkHealth () {
    return 100 * (
      this.edges.reduce((v, e) => v + (e.health / e.maxHealth), 0) / this.totalEdges
    );
  }

  getNetworkHealthString () {
    return `${this.getNetworkHealth().toFixed(0)}%`;
  }

  fireHealthChangeParticle (diff) {
    setTimeout(() => {
      Particles.addParticle({
        ttl: 1000,
        position: {
          x: 650,
          y: 0
        },
        velocity: { y: -20 },
        style: { opacity: [1, 0], scale: [2, 1, 1, 1] },
        contents: `${diff.toFixed(0)}%`
      });
      this.networkHealthElement.innerText = this.getNetworkHealthString();
    }, 500);
  }

  doTravel () {
    // One fewer day
    this.daysLeft -= 1;

    if (this.daysLeft <= 0) {
      this.uiWrapper.classList.remove('active');
      emit('scene-change', 'victory');
    } else {
      this.uiWrapper.classList.add('active');
    }

    this.daysLeftElement.innerText = this.daysLeft;
    // Lose health from edges
    const healthBefore = this.getNetworkHealth();
    this.edges.map(e => {
      e.active = false;
      if (Math.random() < EDGE_DAMAGE_PROBABILITY) {
        e.damage();
      }
    });
    this.edges.filter(e => { return e.health > 0; });
    const healthAfter = this.getNetworkHealth();
    this.fireHealthChangeParticle(healthAfter - healthBefore);

    this.castles.map(c => { c.candidate = false; });
    this.currentCastle.inhabited = false;
    this.currentCastle = this.targetCastle;
    this.targetCastle = undefined;
    this.currentCastle.inhabited = true;

    this.destinationNameElement.innerText = 'None';
    this.linkStrengthElement.innerText = '--%';

    this.edges.map(e => {
      if (e.c1 === this.currentCastle) {
        e.c2.candidate = true;
      } else if (e.c2 === this.currentCastle) {
        e.c1.candidate = true;
      }
    });

    this.travelButton.playAnimation('unpressed');
  }

  renderBackground () {
    this.context.save();
    this.context.fillStyle = this.grassPattern;
    this.context.fillRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
  }

  renderUI () {
    this.context.save();
    this.context.fillStyle = this.uiPattern;
    this.context.fillRect(0, 0, 320, SCREEN_HEIGHT);
    this.context.fillStyle = 'rgba(33, 33, 33, 1.0)';
    this.context.fillRect(320, 0, 4, SCREEN_HEIGHT);
    if (this.targetCastle) {
      this.travelButton.render();
    }
    this.context.restore();
  }

  render () {
    this.renderBackground();
    this.renderUI();
    this.edges.map(e => e.render());
    this.castles.map(c => c.render());
  }
}
